import { bool, string, number } from "prop-types";
import React from "react";
import { twMerge } from "tailwind-merge";
import { useTranslation, Trans } from "@lib/i18n";

import Typography from "@components/shared/Typography";

import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useIsFree2MoveEnabled } from "@hooks/useIsFree2MoveEnabled";
import { withTabNavigation } from "@hoc/withTabNavigation";

import dynamic from "next/dynamic";

const ReactTooltip = dynamic(() => import("react-tooltip"), {
  ssr: false,
});

import InfoIcon from "components/shared/Icons/InfoIcon";
import {
  useDealSelector,
  useIsCEESubsidySelectedSelector,
} from "@redux/reducers/deal";
import {
  useCarteGriseConfigSelector,
  useCEESubsidySelector,
} from "@redux/reducers/appConfig";
import { useIsB2BLeasysSelected } from "@redux/reducers/deal";
import { getCarteGriseAmount } from "@shared/v2/lib/getCarteGriseAmount";
import { getDealCarteGrise } from "@shared/v2/lib/getDealCarteGrise";
import { getIsVehicleRegistrationByCitroenSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCitroenSelected";

const CashLinkTypography = withTabNavigation(Typography);

const CashPriceDetail = ({
  amount,
  currencySymbol,
  priceFormat,
  withDeliveryInfo,
  isQuantityVisible,
  vehicleQuantity,
  isUIv2 = false,
}) => {
  const { t, i18n } = useTranslation();

  const isB2BSelected = useIsB2BSelected();
  const isFree2MoveEnabled = useIsFree2MoveEnabled();
  const isB2BLeasysSelected = useIsB2BLeasysSelected();
  const ceeSubsidySelected = useIsCEESubsidySelectedSelector();
  const ceeSubsidy = useCEESubsidySelector(ceeSubsidySelected);
  const carteGriseConfig = useCarteGriseConfigSelector();
  const deal = useDealSelector();

  const dealCarteGrise = getDealCarteGrise(deal);

  const isB2CCashLinkPresent =
    !isB2BSelected &&
    Boolean(t("price.b2c.cashLink.url")) &&
    Boolean(t("price.b2c.cashLink.label"));

  const isB2BCashLinkPresent =
    isB2BSelected &&
    isFree2MoveEnabled &&
    Boolean(t("price.b2b.cashLink.url")) &&
    Boolean(t("price.b2b.cashLink.label"));

  const isCeeMentionVisible = ceeSubsidy?.enabled && ceeSubsidySelected;
  const isRegistrationByCitroen = getIsVehicleRegistrationByCitroenSelected(
    deal.financeSimulation.vehicleRegistration
  );
  const isCarteGriseMentionVisible =
    carteGriseConfig?.enabled && isRegistrationByCitroen;

  const carteGriseAmount = isB2BSelected ? getCarteGriseAmount(deal) : 0;

  return (
    <div
      className="flex flex-col justify-start items-end z-10 text-white"
      data-id="price-switcher-price-container"
    >
      <div className={twMerge("flex justify-end items-end", isUIv2 && "mb-2")}>
        {isQuantityVisible && (
          <div>
            <div className="text-xxs font-normal mb-2 text-right mr-8">
              {t("price.pricePerVehicle")}
            </div>
            <div className="flex border-r border-r-white pr-2 mr-2">
              <Typography
                className="text-white font-semibold pr-2 text-lg/1 largeMobile:text-2xl/1"
                data-id="price-switcher-formatted-price-per-vehicle"
              >
                {getFormattedPrice(amount, priceFormat, currencySymbol)}
              </Typography>
              {i18n.exists("price.vat") && (
                <Typography className="text-white pt-1 uppercase text-xs/1 smallMobile:text-sm/1">
                  {t("price.vat")}
                </Typography>
              )}
            </div>
          </div>
        )}
        <div>
          {isQuantityVisible && (
            <div className="text-xs font-normal mb-2">
              {t("price.allVehiclesTotal", { count: vehicleQuantity })}
            </div>
          )}
          <div className="flex">
            <Typography
              className="text-white font-semibold pr-2 text-2xl/1 largeMobile:text-3xl/1"
              data-id="price-switcher-formatted-price"
            >
              {getFormattedPrice(
                (isQuantityVisible ? amount * vehicleQuantity : amount) +
                  carteGriseAmount,
                priceFormat,
                currencySymbol
              )}
            </Typography>
            {isUIv2 && (
              <>
                <div className="inline-flex items-center whitespace-nowrap -ml-1">
                  <Typography className="text-white font-semibold pr-4 text-xl/1">
                    {t("price.otr")}
                  </Typography>
                  <div
                    className="-ml-2 pb-1 inline-flex text-white"
                    data-tip
                    data-for="otrtooltip"
                  >
                    <InfoIcon
                      className="size-4 before:text-black after:text-black"
                      size={16}
                      color={"black"}
                    />
                  </div>

                  <ReactTooltip id="otrtooltip" effect="solid">
                    <div style={{ whiteSpace: "normal", maxWidth: 220 }}>
                      {t("price.otrTooltip")}
                    </div>
                  </ReactTooltip>
                </div>
              </>
            )}
            {!isUIv2 && i18n.exists("price.vat") && (
              <Typography className="text-white pt-1 uppercase text-xs/1 smallMobile:text-sm/1 largeMobile:text-lg/1">
                {t("price.vat")}
              </Typography>
            )}
          </div>
        </div>
      </div>
      <div className="w-full">
        {(isCeeMentionVisible || isCarteGriseMentionVisible) && (
          <Typography className="block text-white tracking-wider text-xs text-right font-extrabold whitespace-break-words">
            {isCeeMentionVisible && (
              <span
                className="whitespace-nowrap"
                data-id="price-switcher-ceeinfo-text"
              >
                {t(`price.ceeSubsidy.${ceeSubsidySelected}.priceDetail`)}
                {isCeeMentionVisible && isCarteGriseMentionVisible
                  ? ". "
                  : null}
              </span>
            )}
            {isCarteGriseMentionVisible && (
              <span
                className="whitespace-nowrap"
                data-id="price-switcher-cartegrise-text"
              >
                {t(
                  `price.carteGrise.cash.${
                    dealCarteGrise && isRegistrationByCitroen
                      ? "included"
                      : "excluded"
                  }`
                )}
              </span>
            )}
          </Typography>
        )}
        {!isUIv2 && withDeliveryInfo && (
          <Typography
            className="block text-white tracking-wider text-xxs text-right font-extrabold"
            data-id="price-switcher-deliveryinfo-text"
          >
            <Trans>{t("price.deliveryInfo.priceDetail")}</Trans>
          </Typography>
        )}
        {(isB2CCashLinkPresent || isB2BCashLinkPresent) && (
          <CashLinkTypography
            className="block text-white tracking-wider text-xxs text-right font-extrabold underline cursor-pointer"
            data-id="price-switcher-b2b-cash-link"
            onClick={() =>
              window.open(
                isB2BSelected
                  ? t("price.b2b.cashLink.url")
                  : t("price.b2c.cashLink.url"),
                "_blank"
              )
            }
            isClickable
          >
            <Trans>
              {isB2BSelected
                ? t("price.b2b.cashLink.label") || t("price.b2b.cashLink.url")
                : t("price.b2c.cashLink.label") || t("price.b2c.cashLink.url")}
            </Trans>
          </CashLinkTypography>
        )}
        {isB2BLeasysSelected && (
          <Typography
            className="block text-white tracking-wider text-xxs text-right font-extrabold underline cursor-pointer"
            data-id="price-switcher-leasys-text"
          >
            {t("price.b2b.leasys.label")}
          </Typography>
        )}
      </div>
    </div>
  );
};

CashPriceDetail.propTypes = {
  amount: number.isRequired,
  currencySymbol: string.isRequired,
  priceFormat: string.isRequired,
  withTitle: bool,
  withDeliveryInfo: bool,
  isQuantityVisible: bool,
  vehicleQuantity: number,
  isUIv2: bool,
};

export default CashPriceDetail;
